import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Scissors, Plus, Edit, Trash2, Clock, Euro } from 'lucide-react';

const ServicesTab = ({ services, onRefresh }) => {
  const { API, getAuthHeaders } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', duration: 30, price: 0, category: '', is_active: true
  });

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setForm({
        name: service.name, description: service.description || '',
        duration: service.duration, price: service.price,
        category: service.category || '', is_active: service.is_active
      });
    } else {
      setEditingService(null);
      setForm({ name: '', description: '', duration: 30, price: 0, category: '', is_active: true });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/services`, form, getAuthHeaders());
      }
      onRefresh();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio servizio:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo servizio?')) return;
    try {
      await axios.delete(`${API}/services/${id}`, getAuthHeaders());
      onRefresh();
    } catch (error) {
      console.error('Errore nell\'eliminazione servizio:', error);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="font-outfit">Servizi</CardTitle>
          <CardDescription>Configura i servizi offerti</CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="w-full sm:w-auto min-h-[44px]" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" />Aggiungi Servizio
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="service-name">Nome Servizio *</Label>
                <Input id="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Taglio Donna" required data-testid="service-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-description">Descrizione</Label>
                <Textarea id="service-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Breve descrizione del servizio..." rows={2} data-testid="service-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service-duration">Durata (minuti) *</Label>
                  <Input id="service-duration" type="number" min="5" step="5" value={form.duration} onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 30 })} required data-testid="service-duration-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-price">Prezzo (€) *</Label>
                  <Input id="service-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} required data-testid="service-price-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-category">Categoria</Label>
                <Input id="service-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. Capelli, Unghie, Estetica..." data-testid="service-category-input" />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="service-active">Servizio Attivo</Label>
                <Switch id="service-active" checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} data-testid="service-active-switch" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="save-service-btn">{editingService ? 'Aggiorna' : 'Salva'} Servizio</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {services.length > 0 ? (
          <div className="space-y-3">
            {services.map((service) => (
              <div key={service.id} className={`grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-2 p-3 sm:p-4 rounded-lg border ${service.is_active ? 'border-border' : 'border-muted bg-muted/30'}`} data-testid={`service-item-${service.id}`}>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Scissors className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium break-words">{service.name}</p>
                    {!service.is_active && <Badge variant="secondary">Disattivato</Badge>}
                    {service.category && <Badge variant="outline">{service.category}</Badge>}
                  </div>
                  {service.description && <p className="text-sm text-muted-foreground break-words mt-0.5">{service.description}</p>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{service.duration} min</span>
                    <span className="flex items-center gap-1"><Euro className="w-3 h-3" />€{service.price.toFixed(2)}</span>
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1 flex items-center justify-end gap-2 border-t sm:border-t-0 pt-2 sm:pt-0">
                  <Button variant="outline" size="sm" className="min-h-[40px] sm:min-h-0 sm:w-9 sm:h-9 sm:p-0" onClick={() => openDialog(service)} data-testid={`edit-service-${service.id}`}>
                    <Edit className="w-4 h-4 sm:mr-0 mr-2" /><span className="sm:hidden">Modifica</span>
                  </Button>
                  <Button variant="outline" size="sm" className="min-h-[40px] sm:min-h-0 sm:w-9 sm:h-9 sm:p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`}>
                    <Trash2 className="w-4 h-4 sm:mr-0 mr-2" /><span className="sm:hidden">Elimina</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Scissors className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nessun servizio configurato</p>
            <p className="text-sm text-muted-foreground mt-1">Aggiungi servizi per poter creare appuntamenti</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServicesTab;
